<template>
  <v-row>
    <v-col cols="12">
      <base-card>
        <v-card-title>
          AEP Templates

          <v-spacer />

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          />

          <v-spacer />

          <v-btn
            dark
            color="primary"
            @click="addTemplate()"
          >
            <v-icon class="pr-1">
              mdi-plus
            </v-icon>

            Add Template
          </v-btn>
        </v-card-title>

        <v-data-table
          :search="search"
          :headers="headers"
          :items="projects"
          item-key="id"
          class="elevation-1 table-one"
          multi-sort
          @click:row="goToDetails"
        >
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center">
              <p class="ma-0 font-weight-medium">
                {{ item.name }}
              </p>
            </div>
          </template>

          <template v-slot:item.productionTitle="{ item }">
            <span v-if="isProduction(item)">
              {{ item.productionTitle || '—' }}
            </span>

            <span
              v-else
              class="text--disabled"
            >
              —
            </span>
          </template>

          <template v-slot:item.productionType="{ item }">
            <v-chip
              v-if="isProduction(item)"
              x-small
              outlined
              color="primary"
            >
              {{ formatProductionType(item.productionType) }}
            </v-chip>

            <span
              v-else
              class="text--disabled"
            >
              —
            </span>
          </template>

          <template v-slot:item.created="{ item }">
            <div>
              {{ moment.utc(item.created).format('HH:mm:ss / DD MMM YY') }}
            </div>
          </template>

          <template v-slot:item.prodStatus="{ item }">
            <div class="prod-status-cell">
              <template v-if="isProduction(item)">
                <span class="prod-label">
                  PROD
                </span>

                <v-icon
                  small
                  color="success"
                  class="ml-1"
                >
                  mdi-check-circle
                </v-icon>
              </template>
            </div>
          </template>

          <template v-slot:item.action="{ item }">
            <div class="d-flex">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="error"
                    dark
                    v-bind="attrs"
                    icon
                    :disabled="!isSuper || isProduction(item)"
                    v-on="on"
                    @click.stop="openDeleteDialog(item)"
                  >
                    <v-icon>
                      mdi-trash-can-outline
                    </v-icon>
                  </v-btn>
                </template>

                <span>
                  {{
                    isProduction(item)
                      ? 'Production templates cannot be deleted'
                      : 'Delete'
                  }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </base-card>
    </v-col>

    <!-- Remove Dialog -->
    <v-dialog
      v-model="removeDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5">
          Delete AEP Project {{ removedObj.name }}
        </v-card-title>

        <v-card-text>
          <div class="row">
            <div class="col-md-12">
              Are you sure?
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            depressed
            text
            @click="removeDialog = false"
          >
            No
          </v-btn>

          <v-btn
            depressed
            color="error"
            @click="deleteAEP()"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { processAEPProject } from '@/store/converters'

export default {
  name: 'Templates',

  metaInfo: {
    title: 'Templates',
  },

  data () {
    return {
      search: '',

      headers: [
        {
          text: 'Name',
          align: 'start',
          sortable: false,
          value: 'name',
        },
        {
          text: 'Production Title',
          value: 'productionTitle',
        },
        {
          text: 'Production Type',
          value: 'productionType',
        },
        {
          text: 'Duration',
          value: 'duration',
        },
        {
          text: 'Create Date',
          value: 'created',
        },
        {
          text: '',
          value: 'prodStatus',
          sortable: false,
          align: 'center',
          width: 120,
        },
      ],

      projects: [],

      aepFile: null,
      fileLoading: false,
      fileParsing: false,

      uploadedAepFile: {
        size: 0,
        name: '',
        lastModifiedDate: null,
        url: '',

        project: {
          compositions: [],
        },

        root: {
          name: '',
          compDur: 0,
        },

        props: [],
      },

      polling: null,

      removeDialog: false,
      removedObj: {},
    }
  },

  computed: {
    loading () {
      return this.$store.getters.isDataLoaded
    },

    isSuper () {
      return this.$store.getters.isSuperadmin
    },
  },

  created () {
    this.loadData()

    if (this.isSuper) {
      this.headers.push({
        text: 'Action',
        value: 'action',
        sortable: false,
        align: 'center',
        width: 70,
      })
    }
  },

  beforeDestroy () {
    clearInterval(this.polling)
  },

  methods: {
    addTemplate () {
      this.$router.push({
        name: 'NewTemplate',
      })
    },

    loadData (silent) {
      if (!silent) {
        this.$store.commit('setPageLoader')
      }

      const params = {
        $orderby: '-id',
      }

      const aepReq =
        this.$store.dispatch(
          'aepProjects',
          params,
        )

      Promise.all([
        aepReq,
      ])
        .then((result) => {
          const projs =
            result[0] || []

          const items = []

          for (
            let i = 0;
            i < projs.length;
            i += 1
          ) {
            const item =
              processAEPProject(
                projs[i],
              )

            item.duration =
              Number(
                item.root.compDur || 0,
              ).toFixed(2)

            items.push(item)
          }

          this.projects = items
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.$store.commit(
            'resolvePageLoader',
          )
        })
    },

    openDeleteDialog (aep) {
      this.removeDialog = true
      this.removedObj = aep
    },

    deleteAEP () {
      if (!this.removedObj.id) {
        return
      }

      this.$store.commit(
        'setPageLoader',
      )

      this.$store.dispatch(
        'deleteAEPProject',
        this.removedObj,
      )
        .then(() => {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'The AEP project has been removed',
              timeout: 6000,
              color: 'success',
            },
          )

          this.loadData()
          this.removedObj = {}
        })
        .catch((errorMsg) => {
          this.$store.commit(
            'showSnackbar',
            {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            },
          )
        })
        .finally(() => {
          this.removeDialog = false

          this.$store.commit(
            'resolvePageLoader',
          )
        })
    },

    goToDetails (aep) {
      this.$router.push({
        name: 'TemplateDetails',

        params: {
          template_id: aep.id,
        },
      })
    },

    isProduction (item) {
      return !!item.isProduction
    },

    formatProductionType (type) {
      if (!type) {
        return '—'
      }

      return type
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .table-one {
  thead.v-data-table-header {
    tr {
      &:hover {
        background-color: #f2f3f8;
      }

      th {
        span {
          font-size: 16px;
          color: #304156;
        }
      }
    }

    tr {
      td {
        padding-bottom: 20px;
        padding-top: 20px;
      }
    }
  }

  tbody {
    tr {
      &:hover {
        background-color: #f2f3f8 !important;
        cursor: pointer;
      }
    }
  }
}

.prod-status-cell {
  min-width: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.prod-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
}
</style>
